'use client';

import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { useState, useRef } from 'react';
import { MarginMode } from '@drift-labs/sdk';
import { cm } from 'src/utils/classNames';
import useProtectedMakerSlotsOpen from 'src/hooks/useProtectedMakerSlotsOpen';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import SettingsSwitch from 'src/components/Settings/components/SettingsSwitch';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import Collapsible from 'src/components/Collapsible/Collapsible';
import Chevron from 'src/components/Icons/Chevron';

interface Props {
	label: string;
	marginEnabledChecked: boolean;
	currentMaxLev: number | undefined;
	pmm: boolean;
	reduceOnly: boolean;
	highLeverageEnabled: boolean;
	onChangeEnabled: (newValue: boolean) => void;
	onChangeRatio: (newValue: number | undefined) => void;
	onChangeReduceOnly: (newValue: boolean) => void;
	onChangeMarginMode: (newValue: MarginMode) => void;
	onChangePmm: (newValue: boolean) => void;
	disabled?: boolean;
	textClass?: string;
	tooltipText?: string;
}

type MarginModeValue = 'default' | 'highLeverage';

const MARGIN_MODE_OPTIONS: BaseRadioItemProps<MarginModeValue>[] = [
	{
		id: 'default',
		value: 'default',
		label: 'Normal',
	},
	{
		id: 'highLeverage',
		value: 'highLeverage',
		label: 'High Leverage',
	},
];

const MAX_LEVERAGE_OPTIONS: BaseRadioItemProps<string>[] = [
	{
		id: 'noLimit',
		value: 'noLimit',
		label: 'No Limit',
		className: 'not-sm:px-sm',
	},
	{
		id: '1',
		value: '1',
		label: '1x',
	},
	{
		id: '2',
		value: '2',
		label: '2x',
	},
	{
		id: '5',
		value: '5',
		label: '5x',
	},
	{
		id: '10',
		value: '10',
		label: '10x',
	},
	{
		id: '15',
		value: '15',
		label: '15x',
	},
];

const MarginUpdateRow = ({
	label,
	marginEnabledChecked,
	currentMaxLev,
	pmm,
	reduceOnly,
	highLeverageEnabled,
	onChangeEnabled,
	onChangeRatio,
	onChangeReduceOnly,
	onChangeMarginMode,
	onChangePmm,
	disabled,
	textClass,
	tooltipText,
}: Props) => {
	const [viewMore, setViewMore] = useState(false);
	const [marginEnabledSelection, setMarginEnabledSelection] =
		useState(marginEnabledChecked);
	const [pmmSelection, setPmmSelection] = useState(pmm);
	const { isPrivyNativeAuth } = usePrivyWalletContext();

	const [reduceOnlySelection, setReduceOnlySelection] = useState(reduceOnly);
	const isDevMode = useDevSwitchIsOn();

	// Track the initial value to only hide if it was initially undefined
	const initialMaxLevRef = useRef(currentMaxLev);
	const shouldShowMaxLeverage =
		initialMaxLevRef.current !== undefined || isDevMode;

	const {
		protectedMakerMaxSlots,
		protectedMakerSlotsOpen,
		canJoin: canJoinProtectedMaker,
	} = useProtectedMakerSlotsOpen();

	const updateMarginMode = (value: MarginModeValue) => {
		onChangeMarginMode(
			value === 'highLeverage' ? MarginMode.HIGH_LEVERAGE : MarginMode.DEFAULT
		);
	};

	const updateMaxLeverage = (value: string) => {
		onChangeRatio(value === 'noLimit' ? undefined : parseInt(value, 10));
	};

	return (
		<div
			key={label}
			className="flex w-full flex-col gap-y-lg rounded-sm border border-stroke-divider p-lg typo-b4 text-text-default"
		>
			<Tooltip
				className={cm('flex items-center', textClass)}
				content={tooltipText}
				disabled={!tooltipText}
			>
				<h2 className="typo-t3">{label}</h2>
			</Tooltip>

			<div className="flex w-full items-center justify-between">
				Margin Mode
				{disabled ? (
					<span>{highLeverageEnabled ? 'High Leverage' : 'Normal'}</span>
				) : (
					<RadioGroup.Default
						size="sm"
						variant="card"
						className="sm:gap-x-sm"
						value={highLeverageEnabled ? 'highLeverage' : 'default'}
						onValueChange={updateMarginMode}
						items={MARGIN_MODE_OPTIONS}
					/>
				)}
			</div>

			{/* Hide spot margin switch when Privy-native for now. */}
			{!isPrivyNativeAuth && (
				<SettingsSwitch
					label="Enable Spot Margin"
					checked={marginEnabledSelection}
					onChange={(value: boolean) => {
						setMarginEnabledSelection(value);
						onChangeEnabled(value);
					}}
					disabled={disabled}
				/>
			)}

			<Collapsible.Root>
				<Collapsible.Trigger
					className="ml-auto flex items-center gap-x-xs typo-t4 text-text-default hover:text-text-secondary"
					onClick={() => setViewMore((val) => !val)}
				>
					View More
					<Chevron direction={viewMore ? 'up' : 'down'} />
				</Collapsible.Trigger>
				<Collapsible.Content className="mt-lg flex flex-col gap-y-md">
					{/* Only show max account leverage if they already have one, we will basically be deprecating this */}
					{shouldShowMaxLeverage && (
						<div className="flex w-full flex-col items-start justify-between gap-y-2 sm:flex-row sm:items-center">
							<DefinitionTooltip definitionId="settingsMarginMode">
								Max Leverage
							</DefinitionTooltip>
							{disabled ? (
								currentMaxLev ? (
									`${currentMaxLev}x`
								) : (
									'No Limit'
								)
							) : (
								<RadioGroup.Default
									size="sm"
									variant="card"
									className="sm:gap-x-sm"
									value={currentMaxLev ? `${currentMaxLev}` : 'noLimit'}
									onValueChange={updateMaxLeverage}
									items={MAX_LEVERAGE_OPTIONS}
								/>
							)}
						</div>
					)}

					<SettingsSwitch
						label="Reduce Only"
						checked={reduceOnlySelection}
						onChange={(value: boolean) => {
							setReduceOnlySelection(value);
							onChangeReduceOnly(value);
						}}
						tooltipDefinitionId="settingsReduceOnly"
						disabled={disabled}
					/>
					<SettingsSwitch
						label="Protected Maker Mode"
						checked={pmmSelection}
						onChange={(value: boolean) => {
							setPmmSelection(value);
							onChangePmm(value);
						}}
						tooltipContent={
							<div className="flex flex-col gap-y-sm">
								<span>Automatically update quotes vs. latency arbitrage.</span>
								<span
									className={
										canJoinProtectedMaker
											? 'text-text-positive-green-button'
											: 'text-text-default'
									}
								>
									{protectedMakerSlotsOpen}/{protectedMakerMaxSlots} slots
									available
								</span>
							</div>
						}
						disabled={disabled}
					/>
				</Collapsible.Content>
			</Collapsible.Root>
		</div>
	);
};

export default MarginUpdateRow;
